import React, { useEffect, useMemo, useState } from "react";

export const CompanyPickerPopup = ({
  companies = [],
  onSelect,
  onClose,
  isBusy = false,
}) => {
  const [query, setQuery] = useState("");

  // Lock page scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase();
    return companies.filter((c) => (c?.name || "").toLowerCase().includes(q));
  }, [companies, query]);

  return (
    <div
      className="company-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-popup-title"
      onClick={onClose}
    >
      <style>{css}</style>

      <div
        className="company-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="company-popup-header">
          <div className="company-popup-title-row">
            <span className="company-popup-badge">ADMIN</span>
            <h2 id="company-popup-title" className="company-popup-title">
              Välj företag
            </h2>
          </div>
          <p className="company-popup-subtitle">
            Du är kopplad till {companies.length} {companies.length === 1 ? "företag" : "företag"}.
            Välj vilket du vill administrera.
          </p>

          {/* Search */}
          <div className="company-popup-search">
            <span className="company-popup-search-icon" aria-hidden="true">
              {/* magnifier */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <line x1="20" y1="20" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" />
              </svg>
            </span>
            <input
              autoFocus
              className="company-popup-search-input"
              type="text"
              placeholder="Sök företag…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="company-popup-empty">
            {query ? "Inga träffar på din sökning." : "Inga företag hittades för den här användaren."}
          </div>
        ) : (
          <div className="company-popup-list" role="list">
            {filtered.map((c) => (
              <button
                key={c._id}
                className="company-popup-item"
                onClick={() => onSelect?.(c._id)}
                disabled={isBusy}
                role="listitem"
              >
                <span className="company-popup-item-icon" aria-hidden="true">
                  {/* building icon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M8 7h3v3H8zM13 7h3v3h-3zM8 12h3v3H8zM13 12h3v3h-3z" fill="currentColor" />
                    <path d="M3 16h18" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                </span>
                <div className="company-popup-item-content">
                  <span className="company-popup-name">{c.name}</span>
                  <span className="company-popup-meta">Organisation</span>
                </div>
                <span className="company-popup-chevron" aria-hidden="true">
                  {/* chevron */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="company-popup-actions">
          <button className="company-popup-cancel" onClick={onClose}>
            Avbryt
          </button>
        </div>

        {/* Busy overlay while selecting company */}
        {isBusy && (
          <div className="company-popup-busy">
            <div className="company-popup-busy-card">
              <div className="company-popup-spinner" aria-hidden="true" />
              <div>Väljer företag…</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Embedded CSS (scoped by class names used above)
const css = `
:root {
  --cp-primary: #1f4e8c;
  --cp-accent: #5DADE2;
  --cp-bg: #0b1324;
  --cp-border: #e6eef8;
  --cp-muted: #6b7280;
}

.company-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200000; /* above everything */
  display: grid;
  place-items: center;
  background:
    radial-gradient(60% 80% at 50% 10%, rgba(93,173,226,0.15), transparent 60%),
    radial-gradient(60% 80% at 80% 90%, rgba(31,78,140,0.18), transparent 60%),
    rgba(8, 13, 26, 0.55);
  backdrop-filter: blur(6px);
  animation: cpFadeIn 160ms ease-out;
}

.company-popup-container {
  width: min(92vw, 760px);
  max-height: 86vh;
  background: #fff;
  border: 1px solid var(--cp-border);
  border-radius: 16px;
  box-shadow:
    0 40px 100px rgba(0,0,0,0.25),
    0 8px 24px rgba(0,0,0,0.12);
  padding: 22px 22px 16px;
  display: flex;
  flex-direction: column;
  position: relative;
  animation: cpSlideUp 200ms cubic-bezier(.2,.8,.2,1);
}

.company-popup-header {
  padding-bottom: 10px;
  border-bottom: 1px solid #eef3fa;
}

.company-popup-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.company-popup-badge {
  font-size: 10px;
  letter-spacing: .08em;
  color: #fff;
  background: linear-gradient(135deg, var(--cp-primary), var(--cp-accent));
  padding: 4px 8px;
  border-radius: 999px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(31,78,140,.25);
}

.company-popup-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: var(--cp-primary);
}

.company-popup-subtitle {
  margin: 8px 0 12px;
  color: var(--cp-muted);
  font-size: 13px;
}

.company-popup-search {
  position: relative;
  display: flex;
  align-items: center;
}

.company-popup-search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
}

.company-popup-search-input {
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #e8eef7;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.company-popup-search-input:focus {
  border-color: var(--cp-accent);
  box-shadow: 0 0 0 4px rgba(93,173,226,.15);
}

.company-popup-list {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  overflow: auto;
  padding: 2px;
}

.company-popup-item {
  border: 1px solid #e9eef6;
  background: linear-gradient(180deg, #ffffff, #fbfdff);
  border-radius: 12px;
  padding: 14px 14px;
  display: grid;
  grid-template-columns: 28px 1fr 18px;
  gap: 12px;
  align-items: center;
  cursor: pointer;
  text-align: left;
  transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
}
.company-popup-item:hover {
  transform: translateY(-1px);
  border-color: #d7e6f8;
  box-shadow:
    0 10px 24px rgba(31,78,140,0.10),
    0 2px 6px rgba(0,0,0,0.05);
}
.company-popup-item:active {
  transform: translateY(0);
}
.company-popup-item:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.company-popup-item-icon {
  width: 28px; height: 28px;
  display: grid; place-items: center;
  border-radius: 8px;
  color: var(--cp-primary);
  background: #eef5ff;
}

.company-popup-item-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.company-popup-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-popup-meta {
  font-size: 12px;
  color: #8a93a5;
  margin-top: 2px;
}

.company-popup-chevron {
  color: #9aa7bd;
  display: grid; place-items: center;
}

.company-popup-empty {
  margin: 18px 0;
  color: #334155;
  font-size: 14px;
  padding: 14px;
  border: 1px dashed #d9e4f5;
  border-radius: 12px;
  background: #f7fbff;
}

.company-popup-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #eef3fa;
  margin-top: 14px;
}

.company-popup-cancel {
  border: 1px solid #d7e3f5;
  background: #ffffff;
  color: #334155;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background .12s, box-shadow .12s, transform .05s;
}
.company-popup-cancel:hover {
  background: #f7fbff;
  box-shadow: 0 4px 14px rgba(31,78,140,.12);
}
.company-popup-cancel:active {
  transform: translateY(1px);
}

/* Busy overlay inside modal */
.company-popup-busy {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255,255,255,.65);
  border-radius: 16px;
  backdrop-filter: blur(2px);
}

.company-popup-busy-card {
  display: grid;
  place-items: center;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #e6eef8;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow:
    0 14px 36px rgba(31,78,140,.16),
    0 2px 8px rgba(0,0,0,.06);
  color: #1f2937;
  font-weight: 700;
}

.company-popup-spinner {
  width: 28px; height: 28px;
  border-radius: 50%;
  border: 3px solid #cfe6fb;
  border-top-color: var(--cp-accent);
  animation: cpSpin .8s linear infinite;
}

/* Animations */
@keyframes cpFadeIn {
  from { opacity: 0 } to { opacity: 1 }
}
@keyframes cpSlideUp {
  from { transform: translateY(8px); opacity: .98 }
  to   { transform: translateY(0);   opacity: 1 }
}
@keyframes cpSpin {
  to { transform: rotate(360deg) }
}

/* Narrow screens */
@media (max-width: 480px) {
  .company-popup-container { padding: 18px 16px 12px; }
  .company-popup-item { grid-template-columns: 26px 1fr 16px; }
}
`;
